import { getAPI, postAPI, deleteAPI } from 'services/api/apiAsync';
import type { AppDispatch, RootState } from 'app/store';
import { API_ENDPOINTS } from 'utils/constants';
import { setInvitor } from './invitorSlice';
import { selectInvitorLoaded } from './invitorSelectors';
import type { Invitor } from './types';

// GET /api/client/invitors → the Invitor (with participants[]). Mirrors Angular
// WatchlistService.getInvitorAndStore(): caches (skips if already loaded) and
// swallows errors (e.g. 404 for individual/non-family users) so the store
// simply stays empty.
export default function fetchInvitor() {
  return async (dispatch: AppDispatch, getState: () => RootState) => {
    if (selectInvitorLoaded(getState())) return;
    try {
      const response = await dispatch(
        getAPI({ url: API_ENDPOINTS.invitors }),
      ).unwrap();
      dispatch(setInvitor((response as Invitor) ?? null));
    } catch {
      // Swallowed — no invitor for this user (matches Angular's no-error-handler behavior).
    }
  };
}

// Mutation payload shapes — mirror Angular MyAccountService methods
// (my-account.service.ts adult/child/newAdult endpoints).

// Mirrors Angular's `RelatedAdult` domain model
// (`my-account/domain/related-adult.ts`) — the API's Jackson DTO
// expects the nested `name: { first, last }` shape (same story as
// `AddChildPayload`; flat `firstName` / `lastName` triggers an
// "Unrecognized field" error).
export interface InviteAdultPayload {
  name: { first: string; last: string };
  emailAddress: string;
  relationship: string;
}

// Mirrors Angular's `RelatedChild` domain model
// (`my-account/domain/related-child.ts`) — the API's Jackson DTO
// rejects flat `firstName` / `lastName` fields with
// "Unrecognized field 'firstName', not marked as ignorable" and
// expects the nested `name: { first, last }` shape plus
// `relationship: 'DEPENDENT'`.
export interface AddChildPayload {
  name: { first: string; last: string };
  birthDate: string;
  ssn: string;
  authorization: string;
  relationship: string;
  // ISO `YYYY-MM-DD` sourced from the product offer
  // (`selectOfferServiceEndDateIso`). Only populated on the
  // Minor Defense use-code path; the ordinary add-child flow sends
  // `null` here. Without this on the child record for a MinorDefense
  // use-code redemption, the BE will not trigger the "Activate your
  // IDD Secure Start" activation email.
  serviceEndDate: string | null;
  // Optional MinorDefense use-code carried through from UseCodeScreen.
  useCode?: string;
}

export interface InviteNewAdultPayload {
  agedOutUserId: string;
  emailAddress: string;
}

// POST /api/client/invitors/adults → sends the invite email and returns the
// updated Invitor with the newly-invited adult appended to participants.
export function inviteAdult(payload: InviteAdultPayload) {
  return async (dispatch: AppDispatch) => {
    const response = await dispatch(
      postAPI({ url: API_ENDPOINTS.invitorsAdults, data: payload }),
    ).unwrap();
    dispatch(setInvitor((response as Invitor) ?? null));
    return response as Invitor;
  };
}

// DELETE /api/client/invitors/adults/{id} → removes the adult (or cancels the
// pending invite) and returns the updated Invitor.
export function deleteAdult(participantId: string) {
  return async (dispatch: AppDispatch) => {
    const response = await dispatch(
      deleteAPI({
        url: `${API_ENDPOINTS.invitorsAdultById}/${participantId}`,
      }),
    ).unwrap();
    dispatch(setInvitor((response as Invitor) ?? null));
    return response as Invitor;
  };
}

// POST /api/client/invitors/children → enrolls the child in the family plan
// and returns the updated Invitor.
export function addChild(payload: AddChildPayload) {
  return async (dispatch: AppDispatch) => {
    const response = await dispatch(
      postAPI({ url: API_ENDPOINTS.invitorsChildren, data: payload }),
    ).unwrap();
    dispatch(setInvitor((response as Invitor) ?? null));
    return response as Invitor;
  };
}

// DELETE /api/client/invitors/children/{id} → removes the child from the
// family plan and returns the updated Invitor.
export function deleteChild(participantId: string) {
  return async (dispatch: AppDispatch) => {
    const response = await dispatch(
      deleteAPI({
        url: `${API_ENDPOINTS.invitorsChildById}/${participantId}`,
      }),
    ).unwrap();
    dispatch(setInvitor((response as Invitor) ?? null));
    return response as Invitor;
  };
}

// POST /api/client/invitors/newAdult → invite an aged-out child to
// SecureStart. Body carries `agedOutUserId` so the BE knows which existing
// participant this invite is upgrading.
export function inviteNewAdult(payload: InviteNewAdultPayload) {
  return async (dispatch: AppDispatch) => {
    const response = await dispatch(
      postAPI({ url: API_ENDPOINTS.invitorsNewAdult, data: payload }),
    ).unwrap();
    dispatch(setInvitor((response as Invitor) ?? null));
    return response as Invitor;
  };
}
